import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  Button,
  Search,
  Checkbox,
  InlineLoading,
} from '@carbon/react';
import { useAtom, useAtomValue } from 'jotai';
import classNames from 'classnames';
import { debounce } from 'lodash-es';

import {
  tcUnitColorByModalOpenAtom,
  tcUnitColorByRulesAtom,
  tcUnitColorByNameMapAtom,
  tcUnitsFacetsAtom,
  tcEnabledUnitFiltersAtom,
  tcMapFilterModeAtom,
  tcMapBoundsAtom,
  tcAoiPolygonAtom,
} from './atoms';
import { ColorByConfig, FilterCategory, TcUnitsFacets } from '../types';
import { TC_UNIT_FILTER_CATEGORIES } from './utils';
import { DEFAULT_COLORS } from 'components/color-picker';
import styles from './tc-color-by-modal.module.scss';
import {
  TypeCurveHeader,
  DevelopmentAreasQueryResponse,
} from 'types/api-responses';
import { buildDevelopmentAreasPayload } from '../build-development-areas-payload';
import { tcEffectiveWorkspaceIdAtom } from 'components/type-curve-workspace/atoms';
import request from 'lib/request';

const QUERY_PATH = '/queries/development_areas';

const UNIT_COLOR_BY_CATEGORIES = TC_UNIT_FILTER_CATEGORIES.filter(
  (c) => c.type === 'picklist'
);

interface FacetOption {
  id: string;
  name: string;
}

function getUnitFacetPicklistOptions(
  category: FilterCategory,
  facets: TcUnitsFacets | null
): FacetOption[] {
  if (!facets || !category.facetKey) return [];
  const facet = facets[category.facetKey as keyof TcUnitsFacets];
  if (!facet || !('buckets' in facet)) return [];
  return facet.buckets
    .map((b) => ({ id: String(b.id), name: b.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

const CategoryList = ({
  activeCategory,
  onSelect,
}: {
  activeCategory: string;
  onSelect: (id: string) => void;
}) => {
  const [search, setSearch] = useState('');
  const effectiveWorkspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const categories = UNIT_COLOR_BY_CATEGORIES.filter(
    (cat) => effectiveWorkspaceId !== null || cat.id !== 'verificationStatus'
  ).filter((cat) => cat.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className={styles.categoriesInner}>
      <div className={styles.categoriesSearch}>
        <Search
          labelText="Search filters"
          placeholder="Search Filter Names"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <ul className={styles.categoryList}>
        {categories.map((cat) => (
          <li key={cat.id} className={styles.categoryItem}>
            <button
              type="button"
              onClick={() => onSelect(cat.id)}
              className={classNames(styles.categoryButton, {
                [styles.categoryButtonActive]: activeCategory === cat.id,
              })}
            >
              {cat.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

function getValueColor(
  value: string,
  index: number,
  existingColors: Record<string, string>
): string {
  return existingColors[value] ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length];
}

const AttributeForm = ({
  activeCategory,
  selectedValues,
  onChange,
  facets,
  existingValueColors,
}: {
  activeCategory: string;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  facets: TcUnitsFacets | null;
  existingValueColors: Record<string, string>;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const category = UNIT_COLOR_BY_CATEGORIES.find(
    (c) => c.id === activeCategory
  );
  if (!category) return null;

  const options = getUnitFacetPicklistOptions(category, facets);

  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredIds = filteredOptions.map((o) => o.id);

  const allSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => selectedValues.includes(id));

  const handleSelectAll = () => {
    if (allSelected) {
      onChange(selectedValues.filter((v) => !filteredIds.includes(v)));
    } else {
      onChange([...new Set([...selectedValues, ...filteredIds])]);
    }
  };

  const handleClear = () => {
    onChange([]);
  };

  return (
    <div className={styles.formWrapper}>
      <h4 className={styles.formTitle}>{category.label}</h4>
      <div className={styles.distinctColors}>
        <span className={styles.distinctColorsLabel}>Distinct Colors</span>
        {selectedValues.length === 0 ? (
          <span className={styles.distinctColorsEmpty}>No values selected</span>
        ) : (
          <div className={styles.distinctColorsList}>
            {selectedValues.map((val, idx) => (
              <div key={val} className={styles.distinctColorRow}>
                <span
                  className={styles.distinctColorSwatch}
                  style={{
                    backgroundColor: getValueColor(
                      val,
                      idx,
                      existingValueColors
                    ),
                  }}
                />
                <span className={styles.distinctColorName}>{val}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={styles.formContent}>
        <Search
          labelText={`Search ${category.label.toLowerCase()}`}
          placeholder={`Search ${category.label.toLowerCase()}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="sm"
        />
        <div className={styles.selectRow}>
          <button
            type="button"
            className={styles.selectAllLink}
            onClick={handleSelectAll}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
          <span className={styles.resultCount}>
            Top {filteredOptions.length} results shown
          </span>
        </div>
        <div className={styles.optionsList}>
          {filteredOptions.map((option) => (
            <Checkbox
              key={option.id}
              id={`unit-color-by-${activeCategory}-${option.id}`}
              labelText={option.name}
              checked={selectedValues.includes(option.id)}
              onChange={(_evt, { checked }) => {
                onChange(
                  checked
                    ? [...selectedValues, option.id]
                    : selectedValues.filter((v) => v !== option.id)
                );
              }}
            />
          ))}
          {filteredOptions.length === 0 && (
            <p className={styles.noResults}>No options available</p>
          )}
        </div>
      </div>
      <div className={styles.formFooter}>
        <button
          type="button"
          className={styles.clearLink}
          onClick={handleClear}
          disabled={selectedValues.length === 0}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export const TcUnitColorByModal = ({
  editingRuleId,
  onClose,
  typeCurveHeader,
}: {
  editingRuleId?: string | null;
  onClose?: () => void;
  typeCurveHeader?: TypeCurveHeader | null;
}) => {
  const [isOpen, setIsOpen] = useAtom(tcUnitColorByModalOpenAtom);
  const [rules, setRules] = useAtom(tcUnitColorByRulesAtom);
  const facets = useAtomValue(tcUnitsFacetsAtom);
  const [existingNameMap, setNameMap] = useAtom(tcUnitColorByNameMapAtom);

  // Read shared filter atoms for color-by unit fetch
  const filters = useAtomValue(tcEnabledUnitFiltersAtom);
  const mapFilterMode = useAtomValue(tcMapFilterModeAtom);
  const mapBounds = useAtomValue(tcMapBoundsAtom);
  const aoiPolygon = useAtomValue(tcAoiPolygonAtom);

  const editingRule = useMemo(() => {
    return editingRuleId
      ? (rules.find((r) => r.ruleId === editingRuleId) ?? null)
      : null;
  }, [editingRuleId, rules]);

  const [activeCategory, setActiveCategory] = useState<string>(
    UNIT_COLOR_BY_CATEGORIES[0]?.id ?? ''
  );
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  // Local color-by name map fetch state
  const [localNameMap, setLocalNameMap] = useState<Record<string, string[]>>(
    {}
  );
  const [isFetching, setIsFetching] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (editingRule) {
      setActiveCategory(editingRule.attribute);
      setSelectedValues([...editingRule.selectedValues]);
    } else {
      setActiveCategory(UNIT_COLOR_BY_CATEGORIES[0]?.id ?? '');
      setSelectedValues([]);
    }
    setLocalNameMap({});
    setIsFetching(false);
  }, [isOpen, editingRule]);

  // Debounced color-by unit name fetch
  const fetchColorByUnitNames = useCallback(
    async (category: string, values: string[]) => {
      if (!typeCurveHeader || values.length === 0) {
        setLocalNameMap({});
        setIsFetching(false);
        return;
      }

      const cat = TC_UNIT_FILTER_CATEGORIES.find((c) => c.id === category);
      if (!cat) {
        setIsFetching(false);
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsFetching(true);

      const newMap: Record<string, string[]> = {};

      try {
        await Promise.all(
          values.map(async (value) => {
            if (controller.signal.aborted) return;

            const colorByFilter = { ...filters, [cat.param]: [value] };
            const payload = buildDevelopmentAreasPayload({
              typeCurveHeader,
              mapFilterMode,
              mapBounds,
              aoiPolygon,
              filters: colorByFilter,
              typeCurveHeaderDetailsId: typeCurveHeader?.id,
              page: 1,
              pageSize: 1,
            });

            const response = await request.post<
              DevelopmentAreasQueryResponse,
              unknown
            >(QUERY_PATH, { query: payload }, controller);

            if (controller.signal.aborted) return;
            if (response.type !== 'success') return;

            const names = (response.data.filteredUnits ?? []).map(
              (u) => u.name
            );
            newMap[`${category}:${value}`] = names;
          })
        );

        if (!controller.signal.aborted) {
          setLocalNameMap(newMap);
        }
      } finally {
        if (!controller.signal.aborted) setIsFetching(false);
      }
    },
    [typeCurveHeader, filters, mapFilterMode, mapBounds, aoiPolygon]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((category: string, values: string[]) => {
      void fetchColorByUnitNames(category, values);
    }, 500),
    [fetchColorByUnitNames]
  );

  // Trigger debounced fetch when selected values or active category change
  useEffect(() => {
    if (!isOpen) return;
    if (selectedValues.length === 0) {
      setLocalNameMap({});
      setIsFetching(false);
      return;
    }
    setIsFetching(true);
    debouncedFetch(activeCategory, selectedValues);
    return () => {
      debouncedFetch.cancel();
    };
  }, [isOpen, activeCategory, selectedValues, debouncedFetch]);

  const handleApply = () => {
    if (isFetching) return;

    const existingValueColors = editingRule?.valueColors ?? {};
    const valueColors: Record<string, string> = { ...existingValueColors };
    selectedValues.forEach((val, idx) => {
      if (!valueColors[val]) {
        valueColors[val] = DEFAULT_COLORS[idx % DEFAULT_COLORS.length];
      }
    });
    // Remove colors for deselected values
    for (const key of Object.keys(valueColors)) {
      if (!selectedValues.includes(key)) delete valueColors[key];
    }

    const newRule: ColorByConfig = {
      ruleId:
        editingRule?.ruleId ??
        `unit-${activeCategory}-${Date.now()}-${Math.round(
          Math.random() * 1000
        )}`,
      attribute: activeCategory,
      selectedValues,
      valueColors,
      applyTo: {
        map: true,
        probitPlot: false,
        monthlyVolumes: false,
        ratios: false,
        crossPlot: false,
      },
      enabled: editingRule?.enabled ?? true,
    };

    if (editingRule) {
      setRules(
        rules.map((r) => (r.ruleId === editingRule.ruleId ? newRule : r))
      );
    } else {
      setRules([...rules, newRule]);
    }

    // Write local name map to global atom (merge with existing)
    setNameMap({ ...existingNameMap, ...localNameMap });

    setIsOpen(false);
    onClose?.();
  };

  // See TcColorByModal.handleCancel — bailing out here desyncs ComposedModal's
  // internal open state from the atom and the modal can never reopen.
  const handleCancel = () => {
    abortRef.current?.abort();
    setIsFetching(false);
    setIsOpen(false);
    onClose?.();
  };

  const handleReset = () => {
    setSelectedValues([]);
  };

  return (
    <ComposedModal
      open={isOpen}
      onClose={handleCancel}
      size="lg"
      className={styles.modal}
    >
      <ModalHeader title="Color Units by Attribute" />
      <ModalBody>
        <div className={styles.content}>
          <div className={styles.categories}>
            <CategoryList
              activeCategory={activeCategory}
              onSelect={(id) => {
                setActiveCategory(id);
                if (!editingRule || editingRule.attribute !== id) {
                  setSelectedValues([]);
                }
              }}
            />
          </div>
          <div className={styles.form}>
            <AttributeForm
              key={activeCategory}
              activeCategory={activeCategory}
              selectedValues={selectedValues}
              onChange={setSelectedValues}
              facets={facets}
              existingValueColors={editingRule?.valueColors ?? {}}
            />
            {isFetching && <InlineLoading description="Loading unit data..." />}
          </div>
        </div>
      </ModalBody>
      <div className={styles.customFooter}>
        <Button
          size="lg"
          kind="ghost"
          className={styles.footerButton}
          onClick={handleCancel}
          disabled={isFetching}
        >
          Cancel
        </Button>
        <Button
          size="lg"
          kind="secondary"
          className={styles.footerButton}
          onClick={handleReset}
        >
          Reset
        </Button>
        <Button
          size="lg"
          kind="primary"
          className={styles.footerButton}
          onClick={handleApply}
          disabled={isFetching}
        >
          {isFetching ? 'Loading...' : 'Apply'}
        </Button>
      </div>
    </ComposedModal>
  );
};
